// Delegate — the headless hook moved to `@heidi/tasks/data` (OpenSpec
// `monorepo-tasks-data-core` · D2, ARCFE-945). This file owns the app
// reaction (ARCFE-921 ratified mechanism): the fetch-error toast, fired
// through the `onFetchError` port with byte-identical copy and per-attempt
// timing (the legacy toast lived inside the `queryFn` catch).
//
// P0.2 note, preserved not fixed: the copy says "this session" although this
// is the global list query.
import { toast } from '@heidi/design-system/toast';
import { useQueryClinicalTasks as useQueryClinicalTasksCore } from '@heidi/tasks/data';
import { useIntl } from 'react-intl';

import { shouldSuppressDesktopOfflineNetworkErrorToast } from '@/utils/http/desktopOfflineNetwork';
import { logger } from '@/utils/logger/logger';
import type { ClinicalTasksQueryParams } from '@heidi/tasks/contracts';
// Definition moved to `@heidi/tasks/contracts` (MR-TASKS-BIRTH C2);
// import + re-export because delegates and consumers key caches off it.
import { sanitizeError } from '@heidi/runtime/logger';
import { GET_CLINICAL_TASKS } from '@heidi/tasks/contracts';

export { GET_CLINICAL_TASKS };

export const useQueryClinicalTasks = (
  params?: ClinicalTasksQueryParams,
  options?: { enabled?: boolean }
) => {
  const intl = useIntl();

  return useQueryClinicalTasksCore(params, {
    enabled: options?.enabled,
    onFetchError: (error: unknown) => {
      // The package logs `error.response?.data`, which is `undefined` on a raw
      // transport failure (`ERR_NETWORK`). Project through `sanitizeError` so a
      // suppressed toast still leaves a status/code diagnostic in Datadog — and
      // so the raw axios object (URL, Authorization, response body) never does.
      logger.error('Error fetching tasks', sanitizeError(error));
      if (shouldSuppressDesktopOfflineNetworkErrorToast(error)) return;

      toast({
        tone: 'destructive',
        title: intl.formatMessage({
          id: 'vbY2EII0Bh',
          defaultMessage: 'Error fetching tasks',
        }),
        description: intl.formatMessage({
          id: 'QBKjO4Xx3+',
          defaultMessage: 'Could not load tasks for this session.',
        }),
      });
    },
  });
};
